import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Badge,
  Button,
  Code,
  Container,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { DiagnosticEventsTable } from "../../../../components/DiagnosticEventsTable"
import { formatDateTime, formatTime } from "../../../../lib/format-date"
import type { IProps } from "./interfaces"

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function stateColor(state: string): string {
  if (state === "closed") return "success"
  if (state === "half-open") return "warning"
  return "danger"
}

/**
 * Plain-language label for the internal circuit-breaker machine state. "closed"
 * reads like "broken" to a non-engineer when it actually means healthy, so map
 * the raw states to words an IT admin can act on at a glance.
 */
function stateLabel(state: string, i18n: ReturnType<typeof useLingui>["i18n"]): string {
  if (state === "closed") return i18n._(msg`Healthy`)
  if (state === "half-open") return i18n._(msg`Recovering`)
  if (state === "open") return i18n._(msg`Failing`)
  return state
}

/**
 * DiagnosticsView — the dumb render surface for the super-admin system
 * diagnostics page. Receives every query result, the events-tab filter state,
 * and the action callbacks as props; holds no data fetching or auth itself.
 * The smart half (queries, polling, guard) lives in routes/_auth/diagnostics.tsx.
 */
export function DiagnosticsView({
  health,
  embedding,
  sessions,
  orphans,
  logs,
  events,
  eventsTotal,
  filters,
  expandedEventId,
  recoveringOrphanId,
  onFiltersChange,
  onToggleEvent,
  onRecoverOrphan,
  onRefetchLogs,
  onRefetchEvents,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="lg">
        <Trans>System diagnostics</Trans>
      </Title>

      {/* Voiceprint embedding service health */}
      <Paper withBorder p="md" mb="lg">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>
            <Trans>Voiceprint embedding service</Trans>
          </Text>
          {embedding === undefined ? (
            <Badge color="gray" variant="dot">
              <Trans>Loading…</Trans>
            </Badge>
          ) : !embedding.configured ? (
            <Badge color="danger" variant="dot">
              <Trans>Not configured</Trans>
            </Badge>
          ) : embedding.reachable ? (
            <Badge color="success" variant="dot">
              <Trans>Reachable</Trans>
            </Badge>
          ) : (
            <Badge color="danger" variant="dot">
              <Trans>Unreachable</Trans>
            </Badge>
          )}
        </Group>
        {embedding && (
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed">
                <Trans>URL</Trans>
              </Text>
              <Code>{embedding.url ?? "—"}</Code>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                <Trans>Latency</Trans>
              </Text>
              <Text fw={600}>{embedding.latencyMs !== null ? `${embedding.latencyMs} ms` : "—"}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                <Trans>Status</Trans>
              </Text>
              <Text fw={600}>{embedding.status ?? embedding.error ?? "—"}</Text>
            </div>
          </Group>
        )}
        {embedding && !embedding.reachable && (
          <Text size="xs" c="dimmed" mt="xs">
            <Trans>Speaker recognition will silently no-op until the embedding service is reachable.</Trans>
          </Text>
        )}
      </Paper>

      {/* Health Overview */}
      <Paper withBorder p="md" mb="lg">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>
            <Trans>System health</Trans>
          </Text>
          <Badge color="success" variant="dot">
            <Trans>Online</Trans>
          </Badge>
        </Group>
        <Group gap="xl">
          <div>
            <Tooltip label={i18n._(msg`The server instance currently handling requests (Kubernetes pod name).`)}>
              <Text size="xs" c="dimmed" style={{ cursor: "help", width: "fit-content" }}>
                <Trans>Server instance</Trans>
              </Text>
            </Tooltip>
            <Code>{health?.pod}</Code>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              <Trans>Uptime</Trans>
            </Text>
            <Text fw={600}>{formatUptime(health?.uptime ?? 0)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              <Trans>Active sessions</Trans>
            </Text>
            <Text fw={600}>{health?.activeSessions ?? 0}</Text>
          </div>
        </Group>
      </Paper>

      <Tabs
        defaultValue="providers"
        onChange={(value) => {
          if (value === "errors") onFiltersChange({ severity: "error" })
        }}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="providers">
            <Trans>Providers</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="sessions">
            <Trans>Active sessions</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="orphans">
            <Trans>Stuck meetings</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="logs">
            <Trans>Logs</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="errors">
            <Trans>Errors</Trans>
          </Tabs.Tab>
          <Tabs.Tab value="events">
            <Trans>Events</Trans>
          </Tabs.Tab>
        </Tabs.List>

        {/* Providers / Circuit Breaker */}
        <Tabs.Panel value="providers">
          <Stack gap="md">
            <Paper withBorder p="md">
              <Text fw={600} mb="sm">
                <Trans>Configured providers</Trans>
              </Text>
              <Group gap="sm">
                {Object.entries(health?.configuredProviders ?? {}).map(([name, configured]) => (
                  <Badge key={name} color={configured ? "success" : "gray"} variant={configured ? "filled" : "outline"}>
                    {name}: {configured ? i18n._(msg`configured`) : i18n._(msg`not set`)}
                  </Badge>
                ))}
              </Group>
            </Paper>

            <Paper withBorder p="md">
              <Text fw={600} mb="sm">
                <Trans>Automatic failover status</Trans>
              </Text>
              {(health?.circuitBreaker ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">
                  <Trans>No provider activity yet</Trans>
                </Text>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <Trans>Provider</Trans>
                      </Table.Th>
                      <Table.Th>
                        <Trans>Status</Trans>
                      </Table.Th>
                      <Table.Th>
                        <Trans>Failures</Trans>
                      </Table.Th>
                      <Table.Th>
                        <Trans>Consecutive</Trans>
                      </Table.Th>
                      <Table.Th>
                        <Trans>Last failure</Trans>
                      </Table.Th>
                      <Table.Th>
                        <Trans>Last success</Trans>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(health?.circuitBreaker ?? []).map((p) => (
                      <Table.Tr key={p.provider}>
                        <Table.Td>{p.provider}</Table.Td>
                        <Table.Td>
                          <Badge color={stateColor(p.state)} size="sm">
                            {stateLabel(p.state, i18n)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{p.failureCount}</Table.Td>
                        <Table.Td>{p.consecutiveFailures}</Table.Td>
                        <Table.Td>{p.lastFailureAt ? formatTime(i18n, p.lastFailureAt) : "-"}</Table.Td>
                        <Table.Td>{p.lastSuccessAt ? formatTime(i18n, p.lastSuccessAt) : "-"}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* Active Streaming Sessions */}
        <Tabs.Panel value="sessions">
          <Paper withBorder p="md">
            <Text fw={600} mb="sm">
              <Trans>Active streaming sessions</Trans>
            </Text>
            {sessions.length === 0 ? (
              <Text size="sm" c="dimmed">
                <Trans>No active sessions</Trans>
              </Text>
            ) : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      <Trans>Meeting ID</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>User</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Segments</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Duration</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Transcoder</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Adapter</Trans>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sessions.map((s) => (
                    <Table.Tr key={s.meetingId}>
                      <Table.Td>
                        <Code>{s.meetingId.slice(0, 30)}</Code>
                      </Table.Td>
                      <Table.Td>{s.userEmail}</Table.Td>
                      <Table.Td>{s.segmentCount}</Table.Td>
                      <Table.Td>{formatUptime(s.durationSec)}</Table.Td>
                      <Table.Td>
                        <Tooltip
                          label={
                            s.hasTranscoder
                              ? i18n._(msg`Audio is being re-encoded server-side (ffmpeg) before transcription.`)
                              : i18n._(msg`Audio is sent to the provider as-is — no server-side re-encoding.`)
                          }
                        >
                          <Badge color={s.hasTranscoder ? "info" : "gray"} size="xs" style={{ cursor: "help" }}>
                            {s.hasTranscoder ? i18n._(msg`Active`) : i18n._(msg`Built-in`)}
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={s.adapterOpen ? "success" : "danger"} size="xs">
                          {s.adapterOpen ? i18n._(msg`connected`) : i18n._(msg`disconnected`)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>

        {/* Orphan Recovery */}
        <Tabs.Panel value="orphans">
          <Paper withBorder p="md">
            <Text fw={600} mb="sm">
              <Trans>Stuck meetings (no progress for over 10 minutes)</Trans>
            </Text>
            {orphans.length === 0 ? (
              <Text size="sm" c="dimmed">
                <Trans>No orphaned meetings</Trans>
              </Text>
            ) : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      <Trans>Meeting</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Phase</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Last updated</Trans>
                    </Table.Th>
                    <Table.Th>
                      <Trans>Action</Trans>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orphans.map((o) => (
                    <Table.Tr key={o.id}>
                      <Table.Td>{o.subject || o.id.slice(0, 30)}</Table.Td>
                      <Table.Td>
                        <Badge color="warning" size="sm">
                          {o.phase}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{formatDateTime(i18n, o.updated_at)}</Table.Td>
                      <Table.Td>
                        <Button
                          size="compact-xs"
                          variant="light"
                          loading={recoveringOrphanId === o.id}
                          onClick={() => onRecoverOrphan(o.id)}
                        >
                          <Trans>Recover</Trans>
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>

        {/* Logs */}
        <Tabs.Panel value="logs">
          <Paper withBorder p="md">
            <Group justify="space-between" mb="sm">
              <Text fw={600}>
                <Trans>Recent logs</Trans>
              </Text>
              <Button size="compact-xs" variant="subtle" onClick={onRefetchLogs}>
                <Trans>Refresh</Trans>
              </Button>
            </Group>
            <ScrollArea h={500} type="auto">
              <Code block style={{ fontSize: 11, lineHeight: 1.4 }}>
                {logs.join("\n") || i18n._(msg`No logs available`)}
              </Code>
            </ScrollArea>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="errors">
          <Stack gap="md">
            <Paper withBorder p="md">
              <Text size="sm" mb="md">
                <Trans>Error-level events. Open Events for the full log.</Trans>
              </Text>
              <Group justify="flex-end" mb="sm">
                <Button size="compact-sm" variant="subtle" onClick={onRefetchEvents}>
                  <Trans>Refresh</Trans>
                </Button>
              </Group>
              {events.length === 0 ? (
                <Text size="sm" c="dimmed">
                  <Trans>No errors right now</Trans>
                </Text>
              ) : (
                <DiagnosticEventsTable
                  events={events}
                  expandedEventId={expandedEventId}
                  onToggleEvent={onToggleEvent}
                />
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* Events */}
        <Tabs.Panel value="events">
          <Stack gap="md">
            <Paper withBorder p="md">
              <Group gap="sm" mb="md" wrap="wrap">
                <TextInput
                  placeholder={i18n._(msg`Org ID`)}
                  value={filters.orgId}
                  onChange={(e) => onFiltersChange({ orgId: e.currentTarget.value })}
                  style={{ flex: 1, minWidth: 160 }}
                />
                <TextInput
                  placeholder={i18n._(msg`Meeting ID`)}
                  value={filters.meetingId}
                  onChange={(e) => onFiltersChange({ meetingId: e.currentTarget.value })}
                  style={{ flex: 1, minWidth: 160 }}
                />
                <Select
                  placeholder={i18n._(msg`Severity`)}
                  value={filters.severity}
                  onChange={(value) => onFiltersChange({ severity: value })}
                  clearable
                  data={["debug", "info", "warn", "error"]}
                  style={{ minWidth: 120 }}
                />
                <Select
                  placeholder={i18n._(msg`Source`)}
                  value={filters.source}
                  onChange={(value) => onFiltersChange({ source: value })}
                  clearable
                  data={["server", "client"]}
                  style={{ minWidth: 120 }}
                />
                <Button size="compact-sm" variant="subtle" onClick={onRefetchEvents}>
                  <Trans>Refresh</Trans>
                </Button>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                <Trans>{eventsTotal} event(s) found</Trans>
              </Text>
              {events.length === 0 ? (
                <Text size="sm" c="dimmed">
                  <Trans>No events found</Trans>
                </Text>
              ) : (
                <DiagnosticEventsTable
                  events={events}
                  expandedEventId={expandedEventId}
                  onToggleEvent={onToggleEvent}
                />
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Container>
  )
}
